import { useState, useEffect, useMemo } from 'react';
import {
  SessionRosterChange,
  SessionRosterEntry,
  telemetryService,
} from '../services/telemetry/telemetryService';

interface DraftRosterEntry extends SessionRosterEntry {
  isPlaceholder?: boolean;
  isUnmapped?: boolean;
}

interface UseRosterManagementProps {
  rosterData: SessionRosterEntry[];
  masterRoster: any[];
  sessionId: string | null;
  userId: string | null;
  refetch: () => Promise<any>;
}

export const useRosterManagement = ({
  rosterData,
  masterRoster,
  sessionId,
  userId,
  refetch,
}: UseRosterManagementProps) => {
  const [draftRoster, setDraftRoster] = useState<DraftRosterEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const rosterDataKey = useMemo(
    () =>
      JSON.stringify(
        rosterData.map((r) => ({
          id: r.id,
          position: r.position,
          seatId: r.seatId,
          rosterId: r.rosterId,
        }))
      ),
    [rosterData]
  );

  useEffect(() => {
    const normalized = rosterData.map((entry) => ({
      ...entry,
      isPlaceholder: !entry.id,
      isUnmapped: !!(entry.seatId && !entry.rosterId),
    }));
    setDraftRoster(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterDataKey]);

  const rosterOptions = useMemo(() => {
    return masterRoster.map((rower) => {
      const label =
        rower.alias?.trim() ||
        rower.preferredName?.trim() ||
        `${rower.firstName} ${rower.lastName}`.trim();

      return {
        profileId: rower.profileId,
        primaryName: label,
        displayLabel: label,
        firstName: rower.firstName,
        lastName: rower.lastName,
        alias: rower.alias || null,
        preferredName: rower.preferredName || null,
        isUnmapped: false,
      };
    });
  }, [masterRoster]);

  const rosterOptionsMap = useMemo(() => {
    const map = new Map<string, (typeof rosterOptions)[number]>();
    rosterOptions.forEach((option) => {
      map.set(option.profileId, option);
    });
    return map;
  }, [rosterOptions]);

  const updateRosterEntry = (position: number, option: any) => {
    setDraftRoster((prev) =>
      prev.map((entry) => {
        if (entry.position !== position) return entry;
        return {
          ...entry,
          rosterId: option.profileId,
          displayName: option.primaryName,
          firstName: option.firstName,
          lastName: option.lastName,
          isPlaceholder: false,
          isUnmapped: false,
        };
      })
    );
  };

  const unassignRower = async (position: number) => {
    if (!sessionId || !userId) {
      setError('Missing required information to unassign rower.');
      return;
    }

    const existingRower = draftRoster.find((entry) => entry.position === position);

    if (!existingRower?.id) {
      setError('No rower found to unassign.');
      return;
    }

    const sessionSeatId = existingRower.seatId;
    if (!sessionSeatId) {
      setError('Missing session seat ID for this row.');
      return;
    }

    try {
      const change: SessionRosterChange = {
        rowerId: existingRower.id,
        seatPosition: position,
        newRosterId: null,
        newSeatId: sessionSeatId,
        newDisplayName: sessionSeatId,
        newFirstName: null,
        newLastName: null,
        newPreferredName: null,
        previousRosterId: existingRower.rosterId,
        previousSeatId: sessionSeatId,
        previousDisplayName: existingRower.displayName,
      };

      const result = await telemetryService.updateSessionRoster(sessionId, userId, [change]);

      if (result.success) {
        setDraftRoster((prev) =>
          prev.map((entry) =>
            entry.position === position
              ? {
                  ...entry,
                  rosterId: null,
                  displayName: entry.seatId || `SEAT-${entry.position}`,
                  firstName: null,
                  lastName: null,
                  preferredName: null,
                  isUnmapped: true,
                }
              : entry
          )
        );
        setSuccessMessage('Rower unassigned successfully');
        await refetch();
      } else {
        setError('Failed to unassign rower. Please try again.');
      }
    } catch (err) {
      console.error('Error unassigning rower:', err);
      setError(err instanceof Error ? err.message : 'Failed to unassign rower.');
    }
  };

  const saveChanges = async (): Promise<boolean> => {
    if (!sessionId || !userId) {
      setError('Unable to determine session or user identity.');
      return false;
    }

    const changes: SessionRosterChange[] = [];

    draftRoster.forEach((draftEntry) => {
      if (!draftEntry.id) return;

      const original = rosterData.find((entry) => entry.position === draftEntry.position) || null;

      const originalRosterId = original?.rosterId || null;
      const originalDisplayName = original?.displayName || null;
      const originalSeatId = original?.seatId || null;
      const originalId = original?.id && original.id.length > 0 ? original.id : null;

      const newRosterId = draftEntry.rosterId || null;
      const newDisplayName = draftEntry.displayName || null;
      const newSeatId = draftEntry.seatId || null;

      const hasChange =
        originalRosterId !== newRosterId ||
        originalDisplayName !== newDisplayName ||
        originalSeatId !== newSeatId;

      if (!hasChange) return;

      changes.push({
        rowerId: originalId,
        seatPosition: draftEntry.position,
        newRosterId,
        newSeatId,
        newDisplayName,
        newFirstName: draftEntry.firstName || null,
        newLastName: draftEntry.lastName || null,
        previousRosterId: originalRosterId,
        previousSeatId: originalSeatId,
        previousDisplayName: originalDisplayName,
      });
    });

    if (changes.length === 0) {
      setSuccessMessage('No changes to save.');
      return true;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await telemetryService.updateSessionRoster(sessionId, userId, changes);
      if (result.success) {
        setSuccessMessage('Session roster updated successfully.');
        await refetch();
        return true;
      } else {
        setError('Failed to update session roster.');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update session roster.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const resetDraftRoster = () => {
    const normalized = rosterData.map((entry) => ({
      ...entry,
      isPlaceholder: !entry.id,
      isUnmapped: !!(entry.seatId && !entry.rosterId),
    }));
    setDraftRoster(normalized);
  };

  return {
    draftRoster,
    saving,
    error,
    successMessage,
    rosterOptions,
    rosterOptionsMap,
    updateRosterEntry,
    unassignRower,
    saveChanges,
    resetDraftRoster,
    setError,
    setSuccessMessage,
  };
};
